<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex inline-flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="General" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>
        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Details</h3
                        >
                        <p class="mt-2 text-sm text-gray-500">
                            Give your store a name and short description.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:rounded-md">
                        <div
                            class="space-y-4 bg-white px-4 py-5 sm:rounded-md sm:p-6"
                        >
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3 sm:col-span-2">
                                    <base-input-field
                                        type="text"
                                        label="Store name"
                                        tooltip="The name is shown on your store"
                                        id="name"
                                        v-model.trim="settings.name"
                                        :errors="$v.settings.name.$error"
                                    />
                                </div>
                                <base-checkbox
                                    v-model="toggleAdminName"
                                    id="adminNameCheckbox"
                                    label="Internal Name"
                                    caption="Use a different name in the dashboard"
                                />
                                <div
                                    v-if="showAdminName"
                                    class="col-span-3 sm:col-span-2"
                                >
                                    <base-input-field
                                        type="text"
                                        label="Store internal name"
                                        tooltip="The name is shown in the dashboard"
                                        id="adminName"
                                        v-model.trim="settings.adminName"
                                        :errors="$v.settings.adminName.$error"
                                    />
                                </div>
                            </div>

                            <div class="mt-6">
                                <label
                                    for="about"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    About
                                </label>
                                <div class="rounded-md shadow-sm">
                                    <textarea
                                        id="about"
                                        rows="3"
                                        v-model.trim="settings.description"
                                        class="form-input mt-1 block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                    ></textarea>
                                </div>
                                <p class="mt-2 text-sm text-gray-500">
                                    (we recommend 150-300 characters).
                                </p>
                            </div>
                            <div class="col-span-6 sm:col-span-6 lg:col-span-3">
                                <div>
                                    <base-input-field
                                        type="text"
                                        label="Currency"
                                        :disabled="true"
                                        tooltip="This value is set when creating your store, please contact support to change the currency."
                                        id="currency"
                                        :value="currencyCodeFormatted"
                                    />
                                </div>
                            </div>

                            <div class="col-span-6 sm:col-span-6 lg:col-span-3">
                                <div>
                                    <label
                                        for="lang"
                                        class="block text-sm font-medium text-gray-700"
                                    >
                                        Language
                                    </label>
                                    <select
                                        v-model.trim="settings.lang"
                                        id="lang"
                                        name="lang"
                                        class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                    >
                                        <option
                                            v-for="lang of langs"
                                            :key="lang.value"
                                            :value="lang.value"
                                        >
                                            {{ lang.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <base-checkbox
                                v-model="settings.autoLanguage"
                                id="autoLanguage"
                                label="Auto-detect Language"
                                tooltip="Automatically detect and display the store in the customer's browser language"
                            />

                            <timezone-selector
                                class="col-span-6 sm:col-span-6 lg:col-span-3"
                                v-model="settings.timezone"
                            />

                            <div class="grid grid-cols-6 gap-6">
                                <div
                                    class="col-span-6 sm:col-span-3"
                                    @click="
                                        copyToClipboard(
                                            settings.id,
                                            'storekit ID'
                                        )
                                    "
                                >
                                    <base-input-field
                                        label="storekit ID"
                                        :disabled="true"
                                        :value="settings.id"
                                        name="venueId"
                                        tooltip="This is the ID we use to identify your store in storekit, you may be asked by support or an integration partner for this number. (click to copy store ID)"
                                    />
                                </div>
                                <div
                                    class="col-span-6 sm:col-span-3"
                                    @click="
                                        copyToClipboard(
                                            settings.externalId,
                                            'external ID'
                                        )
                                    "
                                >
                                    <base-input-field
                                        label="External ID"
                                        :disabled="true"
                                        :value="settings.externalId"
                                        name="externalId"
                                        tooltip="This is the external ID used for integrations and external systems. (click to copy external ID)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Contacts</h3
                        >
                        <p class="mt-2 text-sm text-gray-500">
                            Your customers will use this information to contact
                            you.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="email_address"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                    >Email address</label
                                >
                                <p class="mb-2 mt-1 text-sm text-gray-500">
                                    This email address will be used to send
                                    order notifications and other important
                                    information. You can add multiple email
                                    addresses by separating them with a comma.
                                </p>
                                <base-tags-input
                                    id="email_address"
                                    v-model="settings.email"
                                    class="mt-1"
                                    :error="$v.settings.email.$error"
                                    :placeholder="
                                        settings.email.length
                                            ? ''
                                            : 'hello@example.com'
                                    "
                                />
                            </div>

                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="phone_number"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                    >Phone number</label
                                >
                                <input
                                    v-model.trim="settings.phoneNumber"
                                    placeholder="020 1234 5678"
                                    type="tel"
                                    id="phone_number"
                                    class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                />
                            </div>

                            <base-input-field
                                id="venueWebsiteURL"
                                label="Website"
                                type="url"
                                class="col-span-6 sm:col-span-4"
                                v-model.trim="settings.url"
                                :errors="$v.settings.url.$error"
                                caption=""
                                errorMessage="Please enter a valid URL starting with https:// or http://"
                                placeholder="eg. https://restaurant.com"
                            />

                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="instagramUsername"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                    >Instagram Username</label
                                >
                                <div class="mt-1 flex rounded-md shadow-sm">
                                    <span
                                        class="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm"
                                    >
                                        https://instagram.com/
                                    </span>
                                    <input
                                        v-model="settings.instagramUsername"
                                        type="text"
                                        name="instagramUsername"
                                        id="instagramUsername"
                                        class="form-input block w-full flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="myinsta"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Store address
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            This address will show on your store and be used to
                            calculate delivery distances.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <label
                                        for="street"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                        >Street address</label
                                    >
                                    <input
                                        v-model.trim="settings.address.street"
                                        placeholder="eg 101 High St"
                                        autocomplete="shipping street-address"
                                        id="street"
                                        class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                        :class="{
                                            'border-red-300':
                                                $v.settings.address.street
                                                    .$error
                                        }"
                                    />
                                </div>

                                <div class="col-span-6">
                                    <label
                                        for="building_number"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                        >Building name / number
                                        <span class="font-normal text-gray-500"
                                            >(Optional)</span
                                        ></label
                                    >
                                    <input
                                        v-model.trim="
                                            settings.address.buildingNumber
                                        "
                                        placeholder="eg 15-17"
                                        id="building_number"
                                        class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                    />
                                </div>

                                <div
                                    class="col-span-6 sm:col-span-6 lg:col-span-3"
                                >
                                    <label
                                        for="city"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                        >City</label
                                    >
                                    <input
                                        id="city"
                                        v-model.trim="settings.address.city"
                                        autocomplete="shipping locality"
                                        placeholder="eg London"
                                        class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                        :class="{
                                            'border-red-300':
                                                $v.settings.address.city.$error
                                        }"
                                    />
                                </div>

                                <div
                                    class="col-span-6 sm:col-span-3 lg:col-span-3"
                                >
                                    <label
                                        for="postal_code"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                        >{{ postCodeLabel }}</label
                                    >
                                    <input
                                        id="postal_code"
                                        v-model.trim="settings.address.postCode"
                                        :placeholder="postCodePlaceholder"
                                        autocomplete="shipping postal-code"
                                        class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                        :class="{
                                            'border-red-300':
                                                $v.settings.address.postCode
                                                    .$error
                                        }"
                                    />
                                </div>

                                <div
                                    v-if="showStateField"
                                    class="col-span-6 sm:col-span-3 lg:col-span-3"
                                >
                                    <label
                                        for="state"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                        >{{ stateLabel }}</label
                                    >
                                    <input
                                        id="state"
                                        v-model.trim="settings.address.state"
                                        :placeholder="statePlaceholder"
                                        autocomplete="shipping address-level1"
                                        class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                        :class="{
                                            'border-red-300':
                                                $v.settings.address.state
                                                    .$error
                                        }"
                                    />
                                </div>
                            </div>
                            <div class="col-span-6 sm:col-span-6 lg:col-span-3">
                                <div>
                                    <label
                                        for="location"
                                        class="block text-sm font-medium text-gray-700"
                                        >Country</label
                                    >
                                    <select
                                        v-model.trim="settings.address.country"
                                        id="location"
                                        name="location"
                                        class="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                        :class="{
                                            'border-red-300':
                                                $v.settings.address.country
                                                    .$error
                                        }"
                                    >
                                        <option
                                            v-for="country in countries"
                                            :key="country.code"
                                            :value="country.code"
                                        >
                                            {{ country.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <base-checkbox
                                v-if="
                                    settings.acceptsPickup ||
                                    settings.acceptsDelivery
                                "
                                v-model="settings.showAddress"
                                :tooltip="`Optionally hide your address from customers before they order.`"
                                id="showAddress"
                                label="Show Address"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Marketing
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Let customers sign up for marketing communications
                            at checkout.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    class="mb-2 block text-sm font-medium leading-5 text-gray-700"
                                    >Show a marketing signup option at checkout
                                </label>
                                <toggle-switch
                                    @input="
                                        settings.optInMessage === ''
                                            ? (settings.optInMessage =
                                                  'Save my details to keep up to date on news and special offers')
                                            : (settings.optInMessage = '')
                                    "
                                    :value="optInEnabled"
                                />
                            </div>
                            <div
                                class="col-span-6 sm:col-span-4"
                                :class="{
                                    'select-none opacity-50': !settings.optInMessage
                                }"
                            >
                                <label
                                    class="mb-2 block text-sm font-medium leading-5 text-gray-700"
                                    >Preselect the signup option
                                </label>
                                <toggle-switch
                                    v-model="settings.optInPreselect"
                                />
                            </div>
                            <div
                                class="col-span-6 sm:col-span-4"
                                :class="{
                                    'select-none opacity-50': !settings.optInMessage
                                }"
                            >
                                <label
                                    for="optInMessage"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                    >Opt-in text</label
                                >
                                <input
                                    type="text"
                                    v-model.trim="settings.optInMessage"
                                    placeholder="Save my details to keep up to date on news and special offers"
                                    id="optInMessage"
                                    class="form-input mt-1 block w-full resize-none rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                />
                                <p class="mt-2 text-sm text-gray-500">
                                    Tip: keep your opt-in message short and easy
                                    to understand.
                                </p>
                            </div>
                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    class="mb-2 block text-sm font-medium leading-5 text-gray-700"
                                    >Hide this store from Google and other
                                    search engines
                                </label>
                                <toggle-switch v-model="settings.noIndex" />
                            </div>
                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="metaTitle"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    Meta title
                                    <span class="text-sm text-gray-400">
                                        (optional)
                                    </span>
                                </label>
                                <p class="mt-1 text-sm text-gray-500">
                                    Used to override the title tag. If left blank, the store name will be used as default title value.
                                </p>
                                <input
                                    id="metaTitle"
                                    v-model.trim="settings.metaTitle"
                                    type="text"
                                    class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                />
                            </div>
                            <div class="col-span-6 sm:col-span-4">
                                <label
                                    for="metaDescription"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    Meta description
                                    <span class="text-sm text-gray-400">
                                        (optional)
                                    </span>
                                </label>
                                <p class="mt-1 text-sm text-gray-500">
                                    Used to override the description tag. If left blank, the store description will be used as default description value.
                                </p>
                                <textarea
                                    id="metaDescription"
                                    v-model.trim="settings.metaDescription"
                                    rows="3"
                                    maxlength="255"
                                    class="form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                        >
                            Legal Information
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Tax identifier
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div class="shadow sm:overflow-hidden sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <label
                                for="taxId"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                VAT Number
                            </label>
                            <input
                                type="text"
                                v-model.trim="settings.taxId"
                                placeholder="XXXXX"
                                id="taxId"
                                class="form-input mt-1 block w-full resize-none rounded-md border border-gray-300 px-3 py-2 shadow-sm transition duration-150 ease-in-out focus:border-blue-300 focus:shadow-outline-blue focus:outline-none sm:text-sm sm:leading-5"
                                :class="{
                                    'border-red-300': $v.settings.taxId.$error
                                }"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                        >
                            Close store
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Prevent customers ordering from this store
                            indefinitely.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0">
                    <div
                        class="border border-dashed border-red-400 bg-red-50 shadow sm:overflow-hidden sm:rounded-md"
                    >
                        <div class="space-y-3 px-4 py-5 sm:p-6">
                            <label
                                for="close"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                Store is
                                {{ settings.enabled ? 'open' : 'closed' }}
                            </label>
                            <toggle-switch
                                :value="settings.enabled"
                                @input="toggle()"
                                :confirm="settings.enabled"
                                :confirm-title="'Confirm disabling this store'"
                                :confirm-description="'Closing this store means customers will be unable to place any order. If you\'d like to temporarily stop taking orders, use the snooze orders feature.'"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
            <span class="inline-flex rounded-md shadow-sm" @click="saveVenue">
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { email, minLength, required, requiredIf, url } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import { FeaturesEnum } from '@/enums';
import currencyMap from '@/helpers/currencies';
import COUNTRIES from '@/helpers/venueLocationCountries.json';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';
import TimezoneSelector from '@/components/formComponents/TimezoneSelector';
import { cleanInstagramUsername } from '@/utils/instagram';
import BaseInputField from '@/components/base/form/BaseInputField.vue';

export default {
    name: 'GeneralSettings',
    props: ['venueId', 'venue'],
    metaInfo: {
        title: 'General Settings'
    },
    data() {
        return {
            isLoading: false,
            settings: { ...this.venue },
            currencies: [
                { label: 'Pounds (£)', value: 'GBP' },
                { label: 'Euro (€)', value: 'EUR' },
                { label: 'US Dollars ($)', value: 'USD' },
                { label: 'Canadian Dollars ($)', value: 'CAD' },
                { label: 'Moroccan dirham (د.م)', value: 'MAD' }
            ],
            countries: COUNTRIES,
            FeaturesEnum,
            toggleAdminName: false,
            langs: [
                {
                    label: 'British English',
                    value: 'en-GB'
                },
                {
                    label: 'American English',
                    value: 'en-US'
                },
                {
                    label: 'Dutch',
                    value: 'nl-NL'
                },
                {
                    label: 'French',
                    value: 'fr-FR'
                },
                {
                    label: 'Italian',
                    value: 'it-IT'
                },
                {
                    label: 'Spanish',
                    value: 'es-ES'
                },
                {
                    label: 'German',
                    value: 'de-DE'
                }
            ]
        };
    },
    components: {
        BaseInputField,
        ToggleSwitch,
        TimezoneSelector
    },
    validations: {
        settings: {
            address: {
                buildingNumber: {},
                street: {
                    required,
                    minLength: minLength(4)
                },
                city: {
                    required
                },
                state: {
                    required: requiredIf(function () {
                        const country = this.settings.address && this.settings.address.country;
                        return ['US', 'AU', 'CA'].includes(country);
                    })
                },
                postCode: {
                    required
                },
                country: {
                    required
                }
            },
            email: {
                $each: {
                    required,
                    email
                }
            },
            name: {
                required,
                minLength: minLength(3)
            },
            adminName: {},
            taxId: {
                minLength: minLength(3)
            },
            url: {
                url
            }
        }
    },
    methods: {
        ...mapActions({
            updateVenueSettings: 'venues/update'
        }),
        async saveVenue() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.errors = 'ERROR';
                return false;
            }

            try {
                this.isLoading = true;

                const {
                    buildingNumber,
                    street,
                    city,
                    state,
                    postCode,
                    country,
                    venueId,
                    id
                } = this.settings.address;

                const payload = {
                    id: this.venueId,
                    name: this.settings.name,
                    adminName: this.settings.adminName,
                    description: this.settings.description,
                    email: this.settings.email,
                    phoneNumber: this.settings.phoneNumber,
                    url: this.settings.url,
                    instagramUsername: this.settings.instagramUsername,
                    currencyCode: this.settings.currencyCode,
                    lang: this.settings.lang,
                    autoLanguage: this.settings.autoLanguage,
                    timezone: this.settings.timezone,
                    address: {
                        buildingNumber,
                        street,
                        city,
                        state,
                        postCode,
                        country,
                        id,
                        venueId
                    },
                    showAddress: this.settings.showAddress,
                    optInMessage: this.settings.optInMessage,
                    optInPreselect: this.settings.optInPreselect,
                    taxId: this.settings.taxId,
                    noIndex: this.settings.noIndex,
                    metaTitle: this.settings.metaTitle,
                    metaDescription: this.settings.metaDescription
                };

                await this.updateVenueSettings(payload);

                this.$notify({
                    group: 'settings',
                    title: 'Settings saved'
                });
            } catch (e) {
                throw new Error(`API ${e}`);
            } finally {
                this.isLoading = false;
            }
        },
        async toggle() {
            if (this.isLoading !== true) {
                this.isLoading = true;
                this.settings.enabled = !this.settings.enabled;

                let payload = {
                    id: this.venueId,
                    enabled: this.settings.enabled
                };

                try {
                    await this.updateVenueSettings(payload);
                } catch (error) {
                    this.enabled = !this.settings.enabled;
                    throw new Error(error);
                } finally {
                    this.isLoading = false;
                }
            }
        },
        async copyToClipboard(value, label) {
            await navigator.clipboard.writeText(value);
            this.$notify({
                group: 'settings',
                title: `Copied ${label}: ${value} to clipboard`
            });
        }
    },
    watch: {
        'settings.instagramUsername': {
            handler(username) {
                if (!username) return;

                const cleaned = cleanInstagramUsername(username);
                if (username !== cleaned) {
                    this.settings.instagramUsername = cleaned;
                }
            },
            immediate: true
        }
    },
    computed: {
        ...mapGetters({
            isFeatureAvailable: 'user/isFeatureAvailable'
        }),
        optInEnabled() {
            return !!this.settings.optInMessage;
        },
        currencyCodeFormatted() {
            const currency = currencyMap(this.settings.currencyCode);

            return `${currency.name} (${currency.ISO})`;
        },
        showAdminName() {
            return this.settings.adminName || this.toggleAdminName;
        },
        showStateField() {
            const countriesWithStates = ['US', 'AU', 'CA'];
            return countriesWithStates.includes(this.settings.address?.country);
        },
        stateLabel() {
            const country = this.settings.address?.country;
            if (country === 'US') return 'State';
            if (country === 'AU') return 'State/Territory';
            if (country === 'CA') return 'Province';
            return 'State';
        },
        statePlaceholder() {
            const country = this.settings.address?.country;
            if (country === 'US') return 'eg California';
            if (country === 'AU') return 'eg New South Wales';
            if (country === 'CA') return 'eg Ontario';
            return '';
        },
        postCodeLabel() {
            return this.settings.address?.country === 'US' ? 'ZIP code' : 'Post code';
        },
        postCodePlaceholder() {
            const country = this.settings.address?.country;
            if (country === 'US') return 'eg 90210';
            if (country === 'AU') return 'eg 2000';
            if (country === 'CA') return 'eg M5V 3L9';
            return 'eg SW1A 1AA';
        }
    },
    mounted() {
        this.toggleAdminName = !!this.settings.adminName;
    }
};
</script>
